import {
  Badge,
  Box,
  Flex,
  Group,
  NumberFormatter,
  Paper,
  Skeleton,
  Stack,
  Text,
  ThemeIcon,
} from '@mantine/core';
import { useTranslation } from '@refinedev/core';

import { IconReceipt2 } from '@tabler/icons-react';

import { safeCastNumber } from 'utils/numberUtils';
import { useProviderDashboardPalette } from './useProviderDashboardPalette';

interface RevenueDistributionCardProps {
  isLoading?: boolean;
  isMobile?: boolean;
  overallTotal?: number;
  publicTotal?: number;
  thirdPartyTotal?: number;
  directPayTotal?: number;
  distributionTotal?: number;
  dateBasis?: string;
  ytdRangeLabel?: string;
}

export const RevenueDistributionCard = ({
  isLoading = false,
  isMobile = false,
  overallTotal = 0,
  publicTotal = 0,
  thirdPartyTotal = 0,
  directPayTotal = 0,
  distributionTotal = 1,
  dateBasis = '',
  ytdRangeLabel = '',
}: RevenueDistributionCardProps) => {
  const { translate } = useTranslation();
  const palette = useProviderDashboardPalette();
  const enumToTitleCase = (str: string) => {
    return str
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <Paper
      p="lg"
      radius="lg"
      className="dashboard-chart"
      style={{
        background: palette.cardBackground,
        border: `1px solid ${palette.cardBorder}`,
      }}
    >
      {isLoading ? (
        <Stack gap="md" data-testid="provider-revenue-summary-skeleton">
          <Skeleton height={20} width={220} />
          <Skeleton height={36} width={260} />
          <Skeleton height={18} width={180} />
          <Skeleton height={10} radius="xl" />
          <Flex gap="md" wrap="wrap" pt={4}>
            <Skeleton height={18} width={120} />
            <Skeleton height={18} width={120} />
            <Skeleton height={18} width={120} />
          </Flex>
        </Stack>
      ) : (
        <Stack gap="md">
          <Flex
            justify="space-between"
            align={isMobile ? 'flex-start' : 'center'}
            direction={isMobile ? 'column' : 'row'}
            gap="sm"
          >
            <Group gap="sm" align="center">
              <ThemeIcon
                size={34}
                radius="md"
                variant="filled"
                style={{
                  background: 'rgba(84, 200, 154, 0.12)',
                  color: '#5AC39A',
                }}
              >
                <IconReceipt2 size={20} />
              </ThemeIcon>
              <Stack gap={2}>
                <Text size="sm" c={palette.subtleText}>
                  {translate(
                    'pages:providerDashboard.metrics.revenueCard.totalRevenueBy',
                    {
                      basis: enumToTitleCase(dateBasis),
                    }
                  )}
                </Text>
                <NumberFormatter
                  prefix="$"
                  value={overallTotal}
                  thousandSeparator
                  decimalScale={2}
                  fixedDecimalScale
                  style={{
                    fontSize: isMobile ? 28 : 32,
                    fontWeight: 800,
                    color: palette.primaryText,
                  }}
                />
                <Badge
                  size="md"
                  radius="xl"
                  variant="filled"
                  style={{
                    background: palette.revenueRangeBadgeBackground,
                    color: palette.revenueRangeBadgeText,
                    textTransform: 'none',
                    fontWeight: 700,
                  }}
                >
                  {ytdRangeLabel}
                </Badge>
              </Stack>
            </Group>
          </Flex>

          <Stack gap={6}>
            <Text size="sm" c={palette.subtleText}>
              {translate(
                'pages:providerDashboard.metrics.revenueCard.distributionTitle',
                'Revenue Distribution'
              )}
            </Text>
            <Group
              gap={0}
              wrap="nowrap"
              style={{
                width: '100%',
                overflow: 'hidden',
                borderRadius: 999,
              }}
            >
              <Box
                style={{
                  height: 10,
                  width: `${(publicTotal / distributionTotal) * 100}%`,
                  minWidth: publicTotal > 0 ? 10 : 0,
                  background: '#59C79A',
                }}
              />
              <Box
                style={{
                  height: 10,
                  width: `${(thirdPartyTotal / distributionTotal) * 100}%`,
                  minWidth: thirdPartyTotal > 0 ? 10 : 0,
                  background: '#4B96F1',
                }}
              />
              <Box
                style={{
                  height: 10,
                  width: `${(directPayTotal / distributionTotal) * 100}%`,
                  minWidth: directPayTotal > 0 ? 10 : 0,
                  background: '#F0B23A',
                }}
              />
            </Group>

            <Flex gap="md" wrap="wrap" pt={4}>
              {[
                [
                  translate(
                    'pages:providerDashboard.card.sections.publicPay',
                    'Public Pay'
                  ),
                  publicTotal,
                  '#59C79A',
                ],
                [
                  translate(
                    'pages:providerDashboard.card.sections.thirdParty',
                    'Third-Party'
                  ),
                  thirdPartyTotal,
                  '#4B96F1',
                ],
                [
                  translate(
                    'pages:providerDashboard.card.sections.directPay',
                    'Direct Pay'
                  ),
                  directPayTotal,
                  '#F0B23A',
                ],
              ].map(([label, value, color]) => (
                <Group key={label} gap={6}>
                  <Box
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 999,
                      background: `${color}`,
                    }}
                  />
                  <Text size="sm" c={palette.mutedText}>
                    {label}
                  </Text>
                  <NumberFormatter
                    prefix="$"
                    value={safeCastNumber(value as number)}
                    thousandSeparator
                    decimalScale={2}
                    fixedDecimalScale
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: palette.primaryText,
                    }}
                  />
                </Group>
              ))}
            </Flex>
          </Stack>
        </Stack>
      )}
    </Paper>
  );
};
